import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { trackExperienceScored } from 'services/engagementTracking'
import { useRevealProps } from '../../MwPage/components/Reveal'
import { IDS } from '../../MwPage/content'
import {
  cyan,
  Eyebrow,
  Hi,
  ice,
  ice2,
  iceLine,
  ink,
  Lede,
  muted,
  paper,
  sans,
  Section,
  SectionHead,
  Small,
  Stack,
} from '../../MwPage/styled'
import TrialCta from '../components/TrialCta'
import { useFocus } from '../focus'
import { Bullets, Card, Column, Grid, H2, Kicker, Wrap } from '../styled'

// the prototype's .pick: a wide white button, cyan once chosen
const Pick = styled.button`
  padding: 15px 20px;
  border-radius: 18px;
  border: 1.5px solid ${iceLine};
  background: ${paper};
  color: ${ink};
  font: 700 16px/1.35 ${sans};
  text-align: left;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;

  &:hover,
  &[aria-pressed='true'] {
    border-color: ${cyan};
    background: ${ice2};
  }
`

// the prototype's .blocker: the answer on a cyan-edged card
const Answer = styled(Card)`
  border: 2px solid ${cyan};
  border-radius: 22px;

  h3 {
    font-size: 23.5px;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 12px;
    color: ${muted};
  }
`

// the prototype's .fixbox: what Jumpstart does about it, beside the button
const FixBox = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 20px;
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 16px;
  background: ${ice};

  p {
    margin: 0;
    max-width: 520px;
    font-weight: 600;
    color: ${ink};
  }
`

/**
 * "Find your #1 blocker": the prototype's seven options; the one tapped names the blocker, what to do first, and
 * what Jumpstart does about it. The tap is the visitor's level for Meta, as a situation chip is on the MW page.
 */
const BlockerSection = ({ offer }) => {
  const { blocker, key: page } = useFocus()
  const [picked, setPicked] = useState(null)
  const answerRef = useRef(null)

  const pick = (option) => {
    if (option.key !== picked) trackExperienceScored({ page, level: option.key })
    setPicked(option.key)
  }

  // the answer lands below the options; once it has rendered, bring it into view when it is not
  useEffect(() => {
    const card = picked && answerRef.current
    if (card && card.scrollIntoView) {
      const rect = card.getBoundingClientRect()
      if (rect.bottom > window.innerHeight || rect.top < 0) card.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [picked])

  const option = blocker.options.find((item) => item.key === picked)

  return (
    <Section id={IDS.where}>
      <Wrap>
        <Stack $gap="clamp(20px, 2.6vw, 26px)">
          <SectionHead>
            <Eyebrow {...useRevealProps()}>{blocker.eyebrow}</Eyebrow>
            <H2 {...useRevealProps()}>
              {blocker.headline} <Hi>{blocker.headlineAccent}</Hi>
            </H2>
            <Lede {...useRevealProps()}>{blocker.lede}</Lede>
          </SectionHead>
          <Column $max={820} $gap={14}>
            <Grid $min={300} role="group" aria-label={blocker.headline} {...useRevealProps()}>
              {blocker.options.map((item) => (
                <Pick key={item.key} type="button" aria-pressed={item.key === picked} onClick={() => pick(item)}>
                  {item.label}
                </Pick>
              ))}
            </Grid>
            {!option && <Small style={{ fontSize: 13.5 }}>{blocker.tapNote}</Small>}
            <div ref={answerRef} aria-live="polite">
              {option && (
                <Answer>
                  <Kicker>{blocker.kicker}</Kicker>
                  <h3>{option.name}</h3>
                  <p>{option.intro}</p>
                  <Bullets>
                    {option.bullets.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </Bullets>
                  <FixBox>
                    <p>
                      {blocker.fix[0]}
                      {option.fix}
                      {blocker.fix[1]}
                    </p>
                    <TrialCta offer={offer} line="fine" />
                  </FixBox>
                </Answer>
              )}
            </div>
          </Column>
        </Stack>
      </Wrap>
    </Section>
  )
}

export default BlockerSection
